import { useRouter } from '@bprogress/next';
import { CategoryOrSubcategoryPageProps } from '@c/CategoryOrSubcategoryPage';
import { SelectOption } from '@c/forms/controls/FormSelect';
import { ListIcon } from '@c/icons';
import { useQuery } from '@tanstack/react-query';
import { getBrands } from '@util/firestore/brands/brands.service';
import { logError } from '@util/logError';
import { serializeBike } from '@util/parseBikeName';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { debounce } from 'lodash';
import { Grid2X2Icon } from 'lucide-react';
import {
  Condition,
  ProductLimitOption,
  ProductSortOption,
  productLimitOptions,
} from 'models/product';
import { Bike, Gender, RideType, SellerType } from 'models/shared';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export type PageType =
  | 'category'
  | 'category1'
  | 'featured'
  | 'brand'
  | 'search'
  | 'all'
  | 'onsale'
  | 'auctions'
  | 'curated'
  | 'profile'
  | 'recent'
  | 'masterProduct';

export type ProductView = 'list' | 'grid' | 'map';

export type PageSideFilters = {
  minPrice: number | null;
  maxPrice: number | null;
  conditions: Condition[];
  brands: string[];
  color?: string[];
  category?: (string | null)[];
  product_cats?: {
    ride_type?: string;
    category?: string;
    category1?: string;
    category2?: string;
    category3?: string;
  };
  genders?: Gender[];
  numberSizes: string[];
  letterSizes: string[];
  tireSizes?: string[];
  onsale: boolean;
  bikeYears?: string[];
  bikeMakes?: string[];
  bikeModels?: string[];
  bikeTypes?: string[];
  bikeEngineSizes?: string[];
  bikeConditions?: Condition[];
  shipTo?: string[];
  shipFrom?: string[];
  sellerType?: SellerType[];
  radius?: number | null;
  location?: { lat: number; lng: number; address: string } | null;
  recentlySold?: boolean;
  mxDirect?: boolean;
  rideTypes?: RideType[];
  is_flat_rate?: boolean;
  aspectRatios?: string[];
  rimDiameters?: string[];
  mountingPositions?: string[];
  tireDiameters?: string[];
  widths?: string[];
  construction?: string[];
};

export type TopFilters = {
  sort: string;
  limit: ProductLimitOption | number;
  view: ProductView;
};

const useProductsPage = ({
  initialTopFilters,
  initialSideFilters,
  current_cat,
  keyword,
}: CategoryOrSubcategoryPageProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFetching(false);
  }, [searchParams, pathname, initialSideFilters, initialTopFilters]);
  // Filters
  const [topFilters, setTopFilters] = useState<TopFilters>(
    initialTopFilters ?? {
      sort: 'bestMatch',
      limit: 24,
      view: 'grid',
    }
  );
  const [sideFilters, setSideFilters] =
    useState<PageSideFilters>(initialSideFilters);

  // the page stays mounted across client navigations, so the filters coming
  // from the server are what tells us the url changed under us — a new search,
  // a suggestion link, back/forward. Local state left alone would keep filters
  // the results are no longer narrowed down by.
  const serverSideFiltersKey = JSON.stringify(initialSideFilters);
  const syncedSideFiltersKey = useRef(serverSideFiltersKey);

  useEffect(() => {
    if (syncedSideFiltersKey.current === serverSideFiltersKey) return;
    syncedSideFiltersKey.current = serverSideFiltersKey;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSideFilters(initialSideFilters);
  }, [serverSideFiltersKey, initialSideFilters]);

  // List / Grid view
  const buttonToggles: ButtonToggleType[] = [
    {
      key: 'grid',
      icon: <Grid2X2Icon className="h-[2.4rem] w-[2.4rem]" />,
      onClick: () => {
        setTopFilters((prev) => ({ ...prev, view: 'grid' }));
        updateUrl(
          sideFilters,
          {
            ...topFilters,
            view: 'grid',
          },
          keyword ?? undefined,
          true
        );
      },
      selected: topFilters.view === 'grid',
      ariaLabel: 'Grid view',
    },
    {
      key: 'list',
      icon: <ListIcon />,
      onClick: () => {
        setTopFilters((prev) => ({ ...prev, view: 'list' }));
        updateUrl(
          sideFilters,
          {
            ...topFilters,
            view: 'list',
          },
          keyword ?? undefined,
          true
        );
      },
      selected: topFilters.view === 'list',
      ariaLabel: 'List view',
    },
  ];

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: () => getBrands(),
  });

  // Filter change handlers
  const handleSideFiltersChange = (filters: PageSideFilters) => {
    setSideFilters({ ...filters });

    if (filters.recentlySold) {
      setTopFilters((prev) => ({ ...prev, view: 'list' }));
    }

    updateUrl(filters, {
      ...topFilters,
      view: filters.recentlySold ? 'list' : topFilters.view,
    });
  };

  const handleLimitChange = (option: SelectOption) => {
    try {
      const parsed = productLimitOptions.parse(Number(option.value));
      setTopFilters((prev) => ({ ...prev, limit: parsed }));
      updateUrl(sideFilters, { ...topFilters, limit: parsed }, undefined, true);
    } catch (e) {
      logError(e);
    }
  };

  const handleSortChange = (option: SelectOption) => {
    try {
      setTopFilters((prev) => ({
        ...prev,
        sort: option.value as ProductSortOption,
      }));
      updateUrl(sideFilters, {
        ...topFilters,
        sort: option.value as ProductSortOption,
      });
    } catch (e) {
      logError(e);
    }
  };

  const handleSearchChange = debounce((keyword: string) => {
    updateUrl(sideFilters, topFilters, keyword);
  }, 1000);

  // Main function to update the URL and fetch new products
  const updateUrl = (
    filters: PageSideFilters,
    topFilters: TopFilters,
    newKeyWord?: string,
    skipIsFetching = false
  ) => {
    const path = getUrl(filters, topFilters, newKeyWord);

    if (!skipIsFetching) setIsFetching(true);
    router.push(path);
    return;
  };

  const getUrl = (
    filters: PageSideFilters,
    topFilters: TopFilters,
    newKeyWord?: string
  ) => {
    if (!pathname) return '';
    let base_path = '';
    let end_path = '';

    if (current_cat) {
      base_path = pathname?.split('/').slice(0, -1).join('/');
      end_path = current_cat.full_slug.split('/').slice(-1)[0];
    } else {
      const parts = pathname.split('/');
      if (parts.length > 4) {
        base_path = pathname?.split('/').slice(0, -1).join('/');
      } else if (parts.length > 3) {
        if (parts.includes('product') || parts.includes('curated')) {
          base_path = pathname?.split('/').join('/');
        } else {
          base_path = pathname?.split('/').slice(0, -1).join('/');
        }
      } else {
        if (parts.includes('search')) {
          base_path = '/search';
        } else {
          base_path = pathname;
        }
      }
      end_path = '';
    }

    // Only category pages can rebuild a filtered url path, since they have a
    // current_cat to anchor the slug to. Every other page keeps its filters in
    // query params so there is no [filters] route for bots to invent
    // arbitrary path segments under.
    const filtersInQuery = !current_cat;

    const urlAdditions = [];
    let foundBrand: string | null = null;

    if (
      filters.conditions &&
      filters.conditions.length === 1 &&
      !filtersInQuery
    ) {
      // technically conditions can only be 1 cause its a radio but just in case
      let condition = filters.conditions[0];
      urlAdditions.push(condition.toLowerCase());
    }
    if (filters.genders && !filtersInQuery) {
      filters.genders.forEach((gender) => {
        const urlGenders = {
          Y: 'youth',
          W: 'women',
          M: 'men',
          U: 'unisex',
        };
        urlAdditions.push(urlGenders[gender].toLowerCase());
      });
    }
    if (filters.numberSizes && !filtersInQuery) {
      filters.numberSizes.forEach((size) => {
        urlAdditions.push(`size-${size.replace('.', '-')}`);
      });
    }
    if (filters.letterSizes && !filtersInQuery) {
      filters.letterSizes.forEach((size) => {
        urlAdditions.push(`size-${size}`);
      });
    }
    if (
      current_cat &&
      !current_cat.is_master_product &&
      filters.brands?.length
    ) {
      let brand = filters.brands.find((b) =>
        brands?.some((brand) => brand.name.toLowerCase() === b.toLowerCase())
      );
      if (brand) {
        brand = brand.replace('%', '-percent').replace(' ', '-');
        brand = brand.replace('&', '');
        brand = brand.replace(`'`, '').replace(')', '');
        brand = brand.replace(' ', '-');
        brand = brand.replace('’', '');
        urlAdditions.push(brand.toLowerCase());
        foundBrand = brand;
      }
    }
    if (filters.onsale && !filtersInQuery) {
      urlAdditions.push('on-sale');
    }
    let path = `${base_path}`;
    let additions_path = urlAdditions.join('-');
    if (additions_path.endsWith('-')) {
      additions_path = additions_path.slice(0, -1);
    }
    if (additions_path.length > 0) {
      if (end_path === 'search') {
        path += `/search/${additions_path}`;
      } else {
        path += `/${additions_path}-${end_path}`;
      }
    } else {
      path += `/${end_path}`;
    }
    if (path.endsWith('-')) {
      path = path.slice(0, -1);
    }
    let query = searchParams;
    let queryParams = [];

    if (filtersInQuery) {
      if (filters.conditions?.length) {
        queryParams.push(`conditions=${filters.conditions.join(',')}`);
      }
      if (filters.genders?.length) {
        queryParams.push(`genders=${filters.genders.join(',')}`);
      }
      if (filters.numberSizes?.length) {
        queryParams.push(
          `numberSizes=${encodeURIComponent(filters.numberSizes.join(','))}`
        );
      }
      if (filters.letterSizes?.length) {
        queryParams.push(
          `letterSizes=${encodeURIComponent(filters.letterSizes.join(','))}`
        );
      }
      if (filters.onsale) {
        queryParams.push('onsale=true');
      }
    }

    if (query?.get('page')) {
      queryParams.push(`page=1`);
    }
    if (newKeyWord) {
      queryParams.push(`keyword=${newKeyWord}`);
    } else if (newKeyWord !== '' && keyword) {
      queryParams.push(`keyword=${keyword}`);
    }
    if (query?.get('tab')) {
      queryParams.push(`tab=${query?.get('tab')}`);
    }
    if (filters.sellerType?.length) {
      queryParams.push(`sellerType=${filters.sellerType}`);
    }
    if (filters.bikeEngineSizes?.length) {
      queryParams.push(`bikeEngineSizes=${filters.bikeEngineSizes.join(',')}`);
    }
    // A page with a category slug spells the place into the path; search has
    // no path to spell it into.
    if (filters.location) {
      queryParams.push(`lat=${filters.location.lat}`);
      queryParams.push(`lng=${filters.location.lng}`);
      if (filters.location.address)
        queryParams.push(`near=${encodeURIComponent(filters.location.address)}`);
      if (filters.radius) queryParams.push(`radius=${filters.radius}`);
    }
    if (filters.shipTo?.length) {
      queryParams.push(`shipTo=${filters.shipTo.join(',')}`);
    }
    if (filters.shipFrom?.length) {
      queryParams.push(`shipFrom=${filters.shipFrom.join(',')}`);
    }
    if (filters.color?.length) {
      queryParams.push(`color=${filters.color}`);
    }
    if (filters.maxPrice) {
      queryParams.push(`maxPrice=${filters.maxPrice}`);
    }
    if (filters.minPrice) {
      queryParams.push(`minPrice=${filters.minPrice}`);
    }
    if (topFilters.limit !== 24) {
      queryParams.push(`limit=${topFilters.limit}`);
    }
    if (filters.recentlySold) {
      queryParams.push(`soldDate=true`);
    }
    if (filters.mxDirect) {
      queryParams.push(`mxDirect=true`);
    }
    if (filters.tireSizes?.length) {
      queryParams.push(`size=${filters.tireSizes.join(',')}`);
    }
    if (filters.is_flat_rate) {
      queryParams.push('is_flat_rate=true');
    }
    const rideTypes = [
      ...(filters?.rideTypes?.filter(Boolean) ?? []),
      ...(filters?.bikeTypes?.filter(Boolean) ?? []),
    ];
    if (rideTypes.length && !current_cat) {
      queryParams.push(`rideTypes=${rideTypes.join(',')}`);
    }
    if (topFilters.view && topFilters.view !== 'grid') {
      queryParams.push(`view=${topFilters.view}`);
    }

    const makes = filters.bikeMakes ?? [];
    const models = filters.bikeModels ?? [];
    const years = filters.bikeYears ?? [];
    const completeBikeCount = Math.min(
      makes.length,
      models.length,
      years.length
    );

    if (completeBikeCount > 0) {
      const parsedBikes = Array.from({ length: completeBikeCount }, (_, i) => {
        return serializeBike({
          year: Number(years[i]),
          make: makes[i] as Bike['make'],
          model: models[i] as Bike['model'],
        });
      });

      queryParams.push(`bikes=${parsedBikes.join(',')}`);
    } else {
      // A make with no model or year behind it is not a bike `bikes=` can
      // spell, and it is the ordinary way to shop for the machine itself.
      if (makes.length) queryParams.push(`bikeMakes=${makes.join(',')}`);
      if (years.length) queryParams.push(`bikeYears=${years.join(',')}`);
      if (models.length) queryParams.push(`bikeModels=${models.join(',')}`);
    }

    if (
      pathname?.includes('recent') &&
      topFilters.sort === 'newest' &&
      !searchParams?.get('sort') &&
      filters.recentlySold
    ) {
      topFilters.sort = 'soldDate';
    }

    if (!filters.recentlySold && topFilters.sort === 'soldDate') {
      topFilters.sort = 'newest';
    }

    if (topFilters.sort !== 'bestMatch') {
      queryParams.push(`sort=${topFilters.sort}`);
    }

    if (
      filters.brands?.length > 1 ||
      (!current_cat && filters.brands.length) ||
      (filters.brands.length && !foundBrand)
    ) {
      if (!current_cat) {
        queryParams.push(
          `brands=${encodeURIComponent(filters.brands.join(','))}`
        );
      } else {
        queryParams.push(
          `brands=${encodeURIComponent(
            foundBrand
              ? filters.brands.filter((b) => b !== foundBrand).join(',')
              : filters.brands.join(',')
          )}`
        );
      }
    }
    if (!!filters.product_cats) {
      if (filters.product_cats.ride_type) {
        queryParams.push(
          `ride_type=${encodeURIComponent(filters.product_cats.ride_type)}`
        );
      }
      if (filters.product_cats.category) {
        queryParams.push(
          `category=${encodeURIComponent(filters.product_cats.category)}`
        );
      }
      if (filters.product_cats.category1) {
        queryParams.push(
          `category1=${encodeURIComponent(filters.product_cats.category1)}`
        );
      }
      if (filters.product_cats.category2) {
        queryParams.push(
          `category2=${encodeURIComponent(filters.product_cats.category2)}`
        );
      }
      if (filters.product_cats.category3) {
        queryParams.push(
          `category3=${encodeURIComponent(filters.product_cats.category3)}`
        );
      }
    }
    if (filters.aspectRatios?.length) {
      queryParams.push(
        `aspectRatios=${encodeURIComponent(filters.aspectRatios.join(','))}`
      );
    }
    if (filters.construction?.length) {
      queryParams.push(
        `construction=${encodeURIComponent(filters.construction.join(','))}`
      );
    }
    if (filters.rimDiameters?.length) {
      queryParams.push(
        `rimDiameters=${encodeURIComponent(filters.rimDiameters.join(','))}`
      );
    }
    if (filters.mountingPositions?.length) {
      queryParams.push(
        `mountingPositions=${encodeURIComponent(
          filters.mountingPositions.join(',')
        )}`
      );
    }
    if (filters.tireDiameters?.length) {
      queryParams.push(
        `tireDiameters=${encodeURIComponent(filters.tireDiameters.join(','))}`
      );
    }
    if (filters.widths?.length) {
      queryParams.push(
        `widths=${encodeURIComponent(filters.widths.join(','))}`
      );
    }

    if (queryParams.length) {
      if (path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      path += `?${queryParams.join('&')}`;
    }
    if (
      !filtersInQuery &&
      ((filters.numberSizes ?? []).length > 1 ||
        (filters.letterSizes ?? []).length > 1 ||
        (filters.genders ?? []).length > 1 ||
        (filters.bikeYears ?? []).length > 1 ||
        (filters.bikeMakes ?? []).length > 1 ||
        (filters.bikeModels ?? []).length > 1 ||
        (filters.shipFrom ?? []).length > 1 ||
        (filters.shipTo ?? []).length > 1) &&
      !path.includes('/filters')
    ) {
      path = '/filters' + path;
    }
    return path;
  };

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleReset = () => {
    setSideFilters(initialSideFilters);
    setTopFilters(initialTopFilters);
    updateUrl(initialSideFilters, initialTopFilters);
  };

  const clearAllFilters = () => {
    const newFilters = {
      conditions: [],
      sellerType: [],
      brands: [],
      numberSizes: [],
      letterSizes: [],
      rideTypes: [],
      minPrice: null,
      maxPrice: null,
      onsale: false,
      mxDirect: false,
      bikeYears: [],
      bikeMakes: [],
      bikeModels: [],
    };
    updateUrl(newFilters, topFilters);
  };

  return {
    buttonToggles,
    sideFilters,
    topFilters,
    handleLimitChange,
    handleSideFiltersChange,
    handleSortChange,
    handleSearchChange,
    drawerOpen,
    setDrawerOpen,
    isFetching,
    setIsFetching,
    handleReset,
    clearAllFilters,
  };
};

export default useProductsPage;
